import React from "react";
import Head from "next/head";
import Link from "next/link";
import { TrendingUp, ArrowRight, CheckCircle, Globe, Shield, BookOpen, Scale, Award } from "lucide-react";
import { BRAND_NAME } from "@/lib/brand";
import RelatedGuides from "@/components/seo/RelatedGuides";

export default function StayCurrentPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#020617]">
      <Head>
        <title>{`Stay Current: Commercial Awareness & Legal News Guide | ${BRAND_NAME}`}</title>
        <meta
          name="description"
          content="Build commercial awareness with Durmah. A structured guide on how legal news affects exams, interviews, and your career in Common Law jurisdictions."
        />
        <link key="route-canonical" rel="canonical" href="https://www.durmah.ai/stay-current" />
        <meta property="og:title" content="Stay Current: Commercial Awareness & Legal News Guide" />
        <meta
          property="og:description"
          content="Build commercial awareness with Durmah. A structured guide on how legal news affects exams, interviews, and your career in Common Law jurisdictions."
        />
        <meta property="og:type" content="article" />
        <meta key="route-og-url" property="og:url" content="https://www.durmah.ai/stay-current" />
      </Head>

      {/* Hero Section */}
      <div className="py-20 lg:py-32 bg-gray-950 text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80')] opacity-20 bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-950 to-gray-950 opacity-80"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-900/30 text-rose-400 text-xs font-bold uppercase tracking-wider mb-6 border border-rose-500/20">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            Core Pillar: Commercial Awareness
          </span>
          <h1 className="text-5xl lg:text-7xl font-black mb-8 tracking-tight">
            Stay Current. <br />
            <span className="text-rose-500 text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-orange-500">Stay Ahead.</span>
          </h1>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Legal education doesn't stop at the library door. {BRAND_NAME} is designed to help you build a steady commercial-awareness habit and connect what you study to what's happening in the real world.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link href="/login?intent=signup">
              <button className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white font-black py-4 px-10 rounded-full transition-all shadow-xl shadow-rose-900/50 flex items-center justify-center gap-2 transform hover:-translate-y-1">
                Start Free Trial <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
            <Link href="/login">
              <button className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white font-bold py-4 px-10 rounded-full transition-all border border-white/10">
                Sign In
              </button>
            </Link>
          </div>
        </div>
      </div>

      <main className="py-24 max-w-4xl mx-auto px-6">
        <article className="prose prose-lg dark:prose-invert max-w-none">
          <section className="mb-20">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 tracking-tight">
              Why Commercial Awareness is Your Competitive Edge
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg mb-8">
              In the modern legal landscape, a strong student knows the law and knows how it is being applied in practice. Whether it&apos;s a landmark Supreme Court ruling or a market-shifting M&A deal, the context of &quot;the now&quot; is what separates top-tier candidates and future partners.
            </p>

            <div className="grid md:grid-cols-2 gap-8 my-16 not-prose">
              <div className="bg-slate-50 dark:bg-white/5 p-8 rounded-3xl border border-slate-100 dark:border-white/10">
                <TrendingUp className="w-10 h-10 text-rose-500 mb-6" />
                <h3 className="text-xl font-bold mb-4 dark:text-white">Academic Performance</h3>
                <p className="text-slate-600 dark:text-gray-400 text-base leading-relaxed">
                  Examiners reward students who cite contemporary developments. Linking a contract law problem to a recent high-court interpretation demonstrates proactive engagement that standard textbooks can&apos;t provide.
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-white/5 p-8 rounded-3xl border border-slate-100 dark:border-white/10">
                <Award className="w-10 h-10 text-emerald-500 mb-6" />
                <h3 className="text-xl font-bold mb-4 dark:text-white">Interview Success</h3>
                <p className="text-slate-600 dark:text-gray-400 text-base leading-relaxed">
                  Firms look for &quot;commerciality.&quot; They need lawyers who understand their clients&apos; business challenges. Discussing a recent regulatory shift shows you&apos;re already thinking like an associate.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6">The Anatomy of Legal Change</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Law is not a collection of static rules; it is a dynamic process. To truly master commercial awareness, one must track changes across several distinct tracks:
            </p>
            <div className="space-y-6 not-prose mb-12">
              <div className="border border-slate-200 dark:border-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Judicial Tracking (Judgments)
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  The Common Law evolves through the principle of <em>stare decisis</em>. Tracking Supreme Court and Court of Appeal judgments via Durmah allows you to see how legal theory is applied to novel facts in real-time.
                </p>
              </div>
              <div className="border border-slate-200 dark:border-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> Statutory & Regulatory Updates
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  From Royal Assent to regulatory guidance issued by the FCA or CMA, legislation provides the framework for commercial activity. Understanding the <em>policy</em> behind new Acts helps you predict where legal risk will fall.
                </p>
              </div>
              <div className="border border-slate-200 dark:border-white/10 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Courts & Tribunals
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Procedure matters. Tracking changes in Court Rules or significant decisions in specialised Tribunals (like the EAT or Upper Tribunal) provides the tactical knowledge required for litigation.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6">The Nuance: Common Law Evolution</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Unlike Civil Law jurisdictions where codes are relatively static, the <strong>Common Law</strong> is a living, breathing entity. Every week, judicial decisions refine the edges of legal doctrine.
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-400 p-6 my-8 not-prose text-gray-900 dark:text-gray-100">
              <div className="flex gap-4">
                <Globe className="w-6 h-6 text-amber-600 shrink-0" />
                <div>
                  <h4 className="font-bold mb-1">Global Context</h4>
                  <p className="text-sm">
                    Developments in the UK Supreme Court often ripple through Australian, Canadian, and Singaporean courts. Commercial awareness is international as much as it is local.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6">How {BRAND_NAME} Solves the &quot;Noise&quot; Problem</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-10">
              The problem isn&apos;t a lack of information; it&apos;s too much of it. A law student shouldn&apos;t have to wade through hours of the FT or the Law Society Gazette to find the developments that actually matter for their modules.
            </p>

            <div className="space-y-8 not-prose">
              {[
                {
                  title: "Selected, not endless",
                  desc: "Durmah is designed to surface stories with genuine legal impact, so you spend less time wading through noise and more time on developments that matter.",
                  icon: <BookOpen className="w-6 h-6 text-rose-500" />,
                },
                {
                  title: "Organised around your syllabus",
                  desc: "Stories are framed around the areas you study (Tort, Equity, Public Law), so it's easier to see where a development might fit into an essay or seminar.",
                  icon: <Scale className="w-6 h-6 text-rose-500" />,
                },
                {
                  title: "Build your own archive",
                  desc: "Save the stories that matter to you. Come exam season or training-contract interviews, your revision is already grounded in real-world examples.",
                  icon: <Shield className="w-6 h-6 text-rose-500" />,
                },
              ].map((feature, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="bg-rose-100 dark:bg-rose-900/20 p-3 rounded-2xl shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{feature.title}</h4>
                    <p className="text-gray-600 dark:text-gray-400">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6">Where to Follow Legal Developments</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {BRAND_NAME} is a way to build the habit, not a replacement for going to the primary sources. If you want to read more widely, these are well-known, reputable places to start:
            </p>
            <ul className="text-gray-600 dark:text-gray-300 space-y-2">
              <li><strong>The UK Supreme Court</strong>: published judgments and case summaries for the most significant appeals.</li>
              <li><strong>Legislation.gov.uk</strong>: the official source for UK statutes and statutory instruments.</li>
              <li><strong>The Law Society Gazette</strong>: news and commentary aimed at the legal profession.</li>
              <li><strong>Regulators such as the FCA and CMA</strong>: guidance and decisions that shape commercial and regulatory law.</li>
              <li><strong>The Financial Times and other quality business press</strong>: for the commercial context behind the law.</li>
            </ul>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-6 italic">
              Commercial awareness is a habit built over time, not a fact to memorise. A few minutes a week, connected back to your modules, goes further than an occasional binge before an interview.
            </p>
          </section>
        </article>

        {/* Final CTA Box */}
        <div className="mt-20 bg-gradient-to-br from-rose-500 to-orange-600 p-12 rounded-[2rem] text-white text-center shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-4xl font-black mb-6">Ready to Prepare More Clearly?</h2>
            <p className="text-rose-100 mb-10 text-lg max-w-xl mx-auto font-medium">
              Use {BRAND_NAME} to connect legal updates with your module
              thinking and seminar preparation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link href="/login?intent=signup">
                <button className="bg-white text-rose-600 font-black py-4 px-12 rounded-full hover:bg-rose-50 transition-all shadow-lg transform hover:scale-105">
                  Start Free Trial Now
                </button>
              </Link>
            </div>
            <p className="mt-8 text-xs text-rose-200 uppercase font-black tracking-widest">
              Already have an account? <Link href="/login" className="underline hover:text-white">Sign In to Continue</Link>
            </p>
          </div>
        </div>

        <div className="mt-24 pt-12 border-t border-slate-100 dark:border-white/10">
          <RelatedGuides currentSlug="stay-current" categories={["News", "Awareness", "Career"]} />
        </div>
      </main>
    </div>
  );
}
